import { CapacitorHttp } from "@capacitor/core";
import { assertPrivateSession, getPrivateSessionState } from "./private-session";
import { isSellerNativeApp } from "@/lib/native/runtime";

// Browser requests and OAuth callbacks must share the seller's host-only
// cookies, even when a build inherited the native API setting. Bundled native
// apps need the configured API host because their local origin serves assets.
const apiBaseUrl = isSellerNativeApp()
  ? ((import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "").replace(/\/+$/, "")
  : "";

// Pathname portion of the base URL — used for same-origin path matching (e.g. session checks).
export const apiBasePath: string = (() => {
  if (!apiBaseUrl) return "/api";
  try {
    return new URL(apiBaseUrl).pathname.replace(/\/+$/, "") || "/api";
  } catch {
    return apiBaseUrl.replace(/\/+$/, "") || "/api";
  }
})();

export function buildRequestUrl(path: string): string {
  const normalizedPath = path.replace(/^\/+/, "");
  const base = apiBaseUrl || `${window.location.origin}/api`;
  return `${base}/${normalizedPath}`;
}

export type ApiFetchClientResponseEvent = {
  path: string;
  requestUrl: URL;
  requestInit: RequestInit;
  response: Response;
};

export type ApiFetchClientResponseListener = (
  event: ApiFetchClientResponseEvent,
) => void | Promise<void>;

const apiFetchClientResponseListeners =
  new Set<ApiFetchClientResponseListener>();

export function subscribeApiFetchClientResponses(
  listener: ApiFetchClientResponseListener,
) {
  apiFetchClientResponseListeners.add(listener);
  return () => {
    apiFetchClientResponseListeners.delete(listener);
  };
}

export async function apiFetchClient(
  path: string,
  options: RequestInit = {},
): Promise<Response> {
  const privateRequest = path.replace(/^\/+/, "").startsWith("seller/") &&
    !/^(?:seller\/auth\/(?:login|logout|switch-store))(?:\?|$)/.test(path.replace(/^\/+/, ""));
  const generation = getPrivateSessionState().generation;
  if (privateRequest) assertPrivateSession(generation);
  const requestUrl = new URL(buildRequestUrl(path));
  const requestInit: RequestInit = { ...options, credentials: "include" };
  let response: Response;
  try {
    const headers = new Headers(requestInit.headers);
    if (isSellerNativeApp() && headers.get("X-Trioty-Meta") === "1" &&
      headers.get("Origin") === "https://seller.trioty.com") {
      // WKWebView strips Origin from Request before Capacitor's fetch patch sees it.
      const result = await CapacitorHttp.request({
        url: requestUrl.toString(), method: requestInit.method ?? "GET",
        headers: Object.fromEntries(headers), data: requestInit.body,
      });
      const responseHeaders = new Headers(result.headers);
      const body = result.status === 204 || result.status === 205 ? null
        : responseHeaders.get("content-type")?.includes("application/json")
          ? JSON.stringify(result.data) : result.data;
      response = new Response(body, { status: result.status, headers: responseHeaders });
      Object.defineProperty(response, "url", { value: result.url });
    } else {
      response = await fetch(requestUrl.toString(), requestInit);
    }
  } catch (error) {
    if (privateRequest) assertPrivateSession(generation);
    throw error;
  }

  if (privateRequest) assertPrivateSession(generation);

  if (apiFetchClientResponseListeners.size > 0) {
    const event: ApiFetchClientResponseEvent = {
      path,
      requestUrl,
      requestInit,
      response,
    };
    for (const listener of [...apiFetchClientResponseListeners]) {
      if (privateRequest) assertPrivateSession(generation);
      try {
        await listener(event);
      } catch {
        // Ignore listener failures to avoid breaking API calls.
      }
      if (privateRequest) assertPrivateSession(generation);
    }
  }

  if (privateRequest) assertPrivateSession(generation);
  return response;
}


/** Narrow recovery read while ordinary private requests are paused. Never cached. */
export function fetchSessionAfterBindingFailure(): Promise<Response> {
  return fetch(buildRequestUrl("seller/auth/me"), { credentials: "include", cache: "no-store", signal: AbortSignal.timeout(5000) });
}

/** Why a first-login replacement was refused (status + server message). */
export class FirstLoginPasswordError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
    this.name = "FirstLoginPasswordError";
  }
}

/** What the replacement answers. `redirectPath` is present when the backend
 *  re-issued the session cookie on this response (roundtable 2, A6): the
 *  seller stays signed in. An older backend answers `{ success: true }`. */
export type FirstLoginPasswordResult = { redirectPath?: string } | null;

/**
 * Required first-login replacement rotates the cookie inside the identity
 * envelope. `currentPassword` is omitted on a first login (the session proves
 * it); an older backend that still requires it answers 400, and the page then
 * asks for the temporary password.
 */
export async function replaceFirstLoginPassword(
  generation: number,
  currentPassword: string | undefined,
  nextPassword: string,
): Promise<FirstLoginPasswordResult> {
  const assertOwned = () => {
    const session = getPrivateSessionState();
    if (session.generation !== generation || !session.changing) throw new DOMException("Session change superseded", "AbortError");
  };
  assertOwned();
  const body = currentPassword ? { currentPassword, nextPassword } : { nextPassword };
  const response = await fetch(buildRequestUrl("seller/password"), { method: "PATCH", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  assertOwned();
  if (!response.ok) {
    let message = "First-login password replacement failed";
    try {
      const payload = (await response.json()) as { message?: unknown };
      const raw = Array.isArray(payload.message) ? payload.message.join(" ") : payload.message;
      if (typeof raw === "string" && raw) message = raw;
    } catch {
      /* no JSON body */
    }
    throw new FirstLoginPasswordError(response.status, message);
  }
  try {
    return (await response.json()) as FirstLoginPasswordResult;
  } catch {
    return null;
  }
}
